import { useEffect, useState } from "react";
import axios from "axios";
import { Trophy, Loader2, ExternalLink, MousePointerClick } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { API } from "@/lib/config";
import { useCountry } from "@/components/contexts/CountryContext";

const PERIODS = [
  { id: "24h", label: "Today" },
  { id: "7d", label: "This week" },
  { id: "30d", label: "This month" },
  { id: "all", label: "All time" },
];

/**
 * Most-clicked videos on this site.
 *
 * Built from our own click log rather than the YouTube chart, so it ranks what
 * visitors here actually opened - the one leaderboard that cannot be copied from
 * the YouTube API.
 */
export const LeaderboardPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { country } = useCountry();
  const period = searchParams.get("period") || "7d";
  const scope = searchParams.get("scope") || "global";

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        const params = { period, limit: 25 };
        if (scope === "country") params.country = country.code;
        const res = await axios.get(`${API}/leaderboard`, { params });
        if (!cancelled) setEntries(res.data.entries || []);
      } catch {
        if (!cancelled) setEntries([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [period, scope, country.code]);

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="leaderboard-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Trophy className="w-6 h-6 text-orange-500" />
          <h1 className="font-heading text-2xl sm:text-3xl font-black">Most Clicked</h1>
        </div>
        <p className="text-sm text-white/50 mb-6">What visitors here actually opened — our data, not YouTube's.</p>

        <div className="flex flex-wrap gap-2 mb-8">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              onClick={() => setSearchParams({ period: p.id, scope }, { replace: true })}
              className={`px-3 py-1.5 min-h-[44px] sm:min-h-0 rounded-lg text-xs font-medium transition-colors ${
                period === p.id ? "bg-orange-500 text-black" : "bg-white/5 border border-white/10 hover:bg-white/10"
              }`}
            >
              {p.label}
            </button>
          ))}
          <button
            onClick={() => setSearchParams({ period, scope: scope === "country" ? "global" : "country" }, { replace: true })}
            className={`ml-auto px-3 py-1.5 min-h-[44px] sm:min-h-0 rounded-lg text-xs font-medium transition-colors ${
              scope === "country" ? "bg-white/15" : "bg-white/5 border border-white/10 hover:bg-white/10"
            }`}
          >
            {scope === "country" ? `${country.flag} ${country.code} only` : "Worldwide"}
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
          </div>
        ) : entries.length === 0 ? (
          <p className="text-center text-sm text-white/40 py-16">
            No clicks recorded for this period yet.
          </p>
        ) : (
          <ol className="space-y-2">
            {entries.map((entry, index) => (
              <li
                key={entry.video_id}
                className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/10 hover:border-orange-500/40 transition-colors"
                data-testid={`leaderboard-row-${entry.video_id}`}
              >
                <span
                  className={`shrink-0 w-8 text-center font-heading font-black ${
                    index < 3 ? "text-orange-500 text-lg" : "text-white/30"
                  }`}
                >
                  {index + 1}
                </span>
                <img
                  src={entry.thumbnail_url}
                  alt=""
                  loading="lazy"
                  className="shrink-0 w-24 sm:w-32 aspect-video object-cover rounded-lg bg-white/5"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-bold line-clamp-2">{entry.title}</p>
                  <p className="text-[11px] text-white/40 truncate">{entry.channel_title}</p>
                  <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-orange-400 font-medium">
                    <MousePointerClick className="w-3 h-3" />
                    {entry.clicks.toLocaleString()} click{entry.clicks === 1 ? "" : "s"}
                  </p>
                </div>
                <a
                  href={entry.video_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 p-2 rounded-lg hover:bg-white/10 text-white/50 hover:text-orange-400 transition-colors"
                  aria-label="Open on YouTube"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
};
